import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Badge, ActionButton } from '@/components/ui'
import { PeriodSelector } from '@/components/analytics'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import {
  listTerritorialManagersNoService,
  listFormerTerritorialManagers,
  listAllManagerPeriodTotalsNoService,
  getManagerPartnerWeeklyRevenue,
  getStudioWeeklyPlanFactRows,
  listMcUsersForTuAdmin,
  setUserTerritorialManager,
} from '@/services/territorialManagerService'
import type {
  TerritorialManagerListItem,
  TerritorialManagerPeriodTotals,
  McUserForTuAdmin,
} from '@/types'
import { formatCurrency, formatNumberRounded, formatPercentage } from '@/lib/utils'
import { Users, UserCog, Building2, Search, Map as MapIcon, Settings, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'

type ManagerKpi = {
  manager_user_id: string
  period_id: number
  period_start: string
  period_end: string
  studio_count: number
  partner_count: number
  fact_gross_revenue_rub: number
  plan_gross_revenue: number
  plan_completion_pct: number | null
  fact_leads_count: number
  plan_leads_count: number | null
  fact_appointments_count: number
  fact_new_visitors_count: number
  fact_new_sales_count: number
  booking_conversion_pct: number | null
  visit_conversion_pct: number | null
  sales_conversion_pct: number | null
  avg_revenue_per_studio: number | null
  avg_revenue_per_partner: number | null
}

function planCompletionTone(pct: number | null): 'success' | 'warning' | 'error' | 'neutral' {
  if (pct == null) return 'neutral'
  if (pct >= 95) return 'success'
  if (pct >= 75) return 'warning'
  return 'error'
}

export function TerritorialManagersPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const periods = useStudioPeriods()
  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    timeFilterOptions,
    selectedRange,
  } = useAnalyticsTimeFilter(periods.currentPeriod)

  const [items, setItems] = useState<TerritorialManagerListItem[]>([])
  const [periodTotals, setPeriodTotals] = useState<TerritorialManagerPeriodTotals[]>([])
  const [weekKpis, setWeekKpis] = useState<Map<string, ManagerKpi>>(new Map())
  const [loading, setLoading] = useState(true)
  const [weekLoading, setWeekLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [isAdvanced, setIsAdvanced] = useState(false)
  const [showRolesAdmin, setShowRolesAdmin] = useState(false)
  const [mcUsers, setMcUsers] = useState<McUserForTuAdmin[]>([])
  const [mcUsersLoading, setMcUsersLoading] = useState(false)
  const [mcUsersError, setMcUsersError] = useState<string | null>(null)
  const [busyUserId, setBusyUserId] = useState<string | null>(null)
  // Снятые с должности: в общий список не входят, но их карточка нужна — там правится дата снятия.
  const [formerItems, setFormerItems] = useState<TerritorialManagerListItem[]>([])
  const [formerOpen, setFormerOpen] = useState(false)

  const reload = () => {
    setLoading(true)
    Promise.all([
      listTerritorialManagersNoService(),
      listAllManagerPeriodTotalsNoService(),
      listFormerTerritorialManagers().catch(() => [] as TerritorialManagerListItem[]),
    ])
      .then(([list, totals, former]) => {
        setItems(list)
        setFormerItems(former)
        setPeriodTotals(totals)
        setError(null)
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }

  const reloadMcUsers = () => {
    setMcUsersLoading(true)
    setMcUsersError(null)
    listMcUsersForTuAdmin()
      .then(setMcUsers)
      .catch(err => setMcUsersError(err instanceof Error ? err.message : String(err)))
      .finally(() => setMcUsersLoading(false))
  }

  useEffect(() => {
    reload()
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!user?.id) return
    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })
    return () => { cancelled = true }
  }, [user?.id, getAccessLevel])

  const myItem = useMemo(
    () => items.find(item => item.manager_user_id === user?.id) ?? null,
    [items, user?.id]
  )

  useEffect(() => {
    let cancelled = false

    const loadWeeklyKpis = async () => {
      if (
        selectedRange?.mode !== 'week' ||
        !periods.currentPeriod?.period_id ||
        !selectedRange.weekNumber ||
        items.length === 0
      ) {
        if (!cancelled) setWeekKpis(new Map())
        return
      }

      try {
        setWeekLoading(true)
        const managerWeeklyRows = await Promise.all(
          items.map(async (item) => ({
            managerUserId: item.manager_user_id,
            rows: await getManagerPartnerWeeklyRevenue(item.manager_user_id, periods.currentPeriod!.period_id),
          }))
        )

        const studioIds = Array.from(
          new Set(
            managerWeeklyRows.flatMap((entry) =>
              entry.rows
                .filter((row) => row.week_number_in_period === selectedRange.weekNumber)
                .map((row) => row.studio_internal_id)
            )
          )
        )

        const weeklyStudioRows = await getStudioWeeklyPlanFactRows({
          studioIds,
          periodId: periods.currentPeriod.period_id,
          weekNumber: selectedRange.weekNumber,
        })
        const weeklyStudioByStudioId = new Map(
          weeklyStudioRows.map((row) => [row.studio_internal_id, row])
        )
        const next = new Map<string, ManagerKpi>()

        managerWeeklyRows.forEach(({ managerUserId, rows }) => {
          const weekRows = rows.filter((row) => row.week_number_in_period === selectedRange.weekNumber)
          const studioIds = new Set(weekRows.map((row) => row.studio_internal_id))
          const partnerIds = new Set(weekRows.map((row) => row.partner_number))
          const factRevenue = weekRows.reduce((sum, row) => sum + (row.gross_revenue_fact_rub || 0), 0)
          const planRevenue = weekRows.reduce((sum, row) => sum + (row.gross_revenue_plan_rub || 0), 0)
          const factLeads = weekRows.reduce(
            (sum, row) => sum + (weeklyStudioByStudioId.get(row.studio_internal_id)?.weekly_leads_fact || 0),
            0
          )
          const planLeads = weekRows.reduce(
            (sum, row) => sum + (weeklyStudioByStudioId.get(row.studio_internal_id)?.weekly_leads_plan || 0),
            0
          )
          const factAppointments = weekRows.reduce(
            (sum, row) => sum + (weeklyStudioByStudioId.get(row.studio_internal_id)?.weekly_appointments_fact || 0),
            0
          )
          const factVisitors = weekRows.reduce(
            (sum, row) => sum + (weeklyStudioByStudioId.get(row.studio_internal_id)?.weekly_new_visitors_fact || 0),
            0
          )
          const factSales = weekRows.reduce((sum, row) => {
            const studioWeek = weeklyStudioByStudioId.get(row.studio_internal_id)
            return sum + (studioWeek?.weekly_new_sales_fact || 0) + (studioWeek?.weekly_new_subscriptions_count_fact || 0)
          }, 0)

          next.set(managerUserId, {
            manager_user_id: managerUserId,
            period_id: periods.currentPeriod.period_id,
            period_start: selectedRange.start,
            period_end: selectedRange.end,
            studio_count: studioIds.size,
            partner_count: partnerIds.size,
            fact_gross_revenue_rub: factRevenue,
            plan_gross_revenue: planRevenue,
            plan_completion_pct: planRevenue > 0 ? (factRevenue / planRevenue) * 100 : null,
            fact_leads_count: factLeads,
            plan_leads_count: planLeads,
            fact_appointments_count: factAppointments,
            fact_new_visitors_count: factVisitors,
            fact_new_sales_count: factSales,
            booking_conversion_pct: factLeads > 0 ? (factAppointments / factLeads) * 100 : null,
            visit_conversion_pct: factAppointments > 0 ? (factVisitors / factAppointments) * 100 : null,
            sales_conversion_pct: factVisitors > 0 ? (factSales / factVisitors) * 100 : null,
            avg_revenue_per_studio: studioIds.size > 0 ? factRevenue / studioIds.size : null,
            avg_revenue_per_partner: partnerIds.size > 0 ? factRevenue / partnerIds.size : null,
          })
        })

        if (!cancelled) setWeekKpis(next)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!cancelled) setWeekLoading(false)
      }
    }

    loadWeeklyKpis()
    return () => { cancelled = true }
  }, [items, periods.currentPeriod?.period_id, selectedRange])

  // Per-manager KPI snapshot for the selected period.
  const kpiByManager = useMemo<Map<string, ManagerKpi>>(() => {
    if (selectedRange?.mode === 'week') return weekKpis
    const selectedPeriodId = periods.currentPeriod?.period_id ?? null
    const grouped = new Map<string, TerritorialManagerPeriodTotals[]>()
    for (const row of periodTotals) {
      const list = grouped.get(row.manager_user_id) ?? []
      list.push(row)
      grouped.set(row.manager_user_id, list)
    }
    const out = new Map<string, ManagerKpi>()
    for (const [managerUserId, rows] of grouped) {
      const selectedRow = selectedPeriodId != null
        ? rows.find((row) => row.period_id === selectedPeriodId) ?? null
        : (rows[0] ?? null)
      if (!selectedRow) continue
      const plan = selectedRow.plan_gross_revenue || 0
      const fact = selectedRow.fact_gross_revenue_rub || 0
      const leads = selectedRow.fact_leads_count || 0
      const planLeads = typeof selectedRow.plan_leads_count === 'number' && Number.isFinite(selectedRow.plan_leads_count)
        ? selectedRow.plan_leads_count
        : null
      const appts = selectedRow.fact_appointments_count || 0
      const visitors = selectedRow.fact_new_visitors_count || 0
      const sales = selectedRow.fact_new_sales_count || 0
      out.set(managerUserId, {
        manager_user_id: managerUserId,
        period_id: selectedRow.period_id,
        period_start: periods.currentPeriod?.period_start ?? '',
        period_end: periods.currentPeriod?.period_end ?? '',
        studio_count: selectedRow.studio_count,
        partner_count: selectedRow.partner_count,
        fact_gross_revenue_rub: fact,
        plan_gross_revenue: plan,
        plan_completion_pct: plan > 0 ? (fact / plan) * 100 : null,
        fact_leads_count: leads,
        plan_leads_count: planLeads,
        fact_appointments_count: appts,
        fact_new_visitors_count: visitors,
        fact_new_sales_count: sales,
        booking_conversion_pct: leads > 0 ? (appts / leads) * 100 : null,
        visit_conversion_pct: appts > 0 ? (visitors / appts) * 100 : null,
        sales_conversion_pct: visitors > 0 ? (sales / visitors) * 100 : null,
        avg_revenue_per_studio: selectedRow.studio_count > 0 ? fact / selectedRow.studio_count : null,
        avg_revenue_per_partner: selectedRow.partner_count > 0 ? fact / selectedRow.partner_count : null,
      })
    }
    return out
  }, [periodTotals, periods.currentPeriod?.period_id, periods.currentPeriod?.period_start, periods.currentPeriod?.period_end, selectedRange?.mode, weekKpis])

  const handleToggleTu = async (u: McUserForTuAdmin, isTm: boolean) => {
    if (!isTm && u.partners_count > 0) {
      const ok = confirm(
        `У пользователя ${u.manager_name || u.user_id.slice(0, 8)} есть ` +
        `${u.partners_count} активных назначений. ` +
        `Последним днём в должности и у франчайзи станет вчерашний день — нового ТУ можно назначить с сегодня. ` +
        `Другой последний день (в т. ч. задним числом или на будущее) можно выбрать и потом поправить в карточке ТУ. Продолжить?`
      )
      if (!ok) return
    }
    setBusyUserId(u.user_id)
    setMcUsersError(null)
    try {
      await setUserTerritorialManager({ userId: u.user_id, isTm })
      reloadMcUsers()
      reload()
    } catch (err) {
      setMcUsersError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyUserId(null)
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(i =>
      i.manager_name.toLowerCase().includes(q) ||
      (i.tg_username ?? '').toLowerCase().includes(q)
    )
  }, [items, search])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UserCog size={20} className="text-primary" />
          <h1 className="text-lg font-semibold">Территориальные управляющие</h1>
        </div>
        {isAdvanced && (
          <div className="flex items-center gap-2">
            <ActionButton
              icon={<Settings size={13} />}
              onClick={() => {
                const next = !showRolesAdmin
                setShowRolesAdmin(next)
                if (next && mcUsers.length === 0) reloadMcUsers()
              }}
            >
              {showRolesAdmin ? 'Скрыть должности' : 'Должности'}
            </ActionButton>
            <ActionButton
              tone="primary"
              icon={<MapIcon size={13} />}
              onClick={() => navigate('/management/partners-coverage')}
            >
              Сопровождение франчайзи
            </ActionButton>
          </div>
        )}
      </div>

      {isAdvanced && showRolesAdmin && (
        <Card>
          <div className="text-sm font-semibold mb-2">Назначение должности «территориальный управляющий»</div>
          <div className="text-xs text-muted-foreground mb-3">
            ТУ может быть сотрудник УК любого уровня доступа — роль определяется должностью,
            а не уровнем. Снятие должности автоматически закрывает все активные назначения
            этого пользователя.
          </div>
          {mcUsersError && (
            <div className="text-xs text-destructive mb-2 flex items-center gap-1">
              <AlertTriangle size={12} /> {mcUsersError}
            </div>
          )}
          {mcUsersLoading && <div className="text-xs text-muted-foreground">Загрузка…</div>}
          <div className="grid grid-cols-1 gap-2">
            {mcUsers.map(u => {
              const isTu = u.job_title === 'territorial_manager'
              const isAdvancedUser = u.access_level === 'advanced'
              return (
                <div
                  key={u.user_id}
                  className="flex items-center justify-between gap-2 py-2 border-b border-border last:border-0"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">
                      {u.manager_name || `#${u.user_id.slice(0, 8)}`}
                      {isAdvancedUser && (
                        <Badge text="advanced" variant="secondary" className="ml-2 text-xs" />
                      )}
                      {isTu && (
                        <Badge text="ТУ" variant="success" className="ml-2 text-xs" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {u.tg_username && <>@{u.tg_username} · </>}
                      {u.email && <>{u.email} · </>}
                      франчайзи: {u.partners_count}
                    </div>
                  </div>
                  <ActionButton
                    tone={isTu ? 'secondary' : 'primary'}
                    disabled={busyUserId === u.user_id}
                    onClick={() => handleToggleTu(u, !isTu)}
                  >
                    {isTu ? 'Снять ТУ' : 'Назначить ТУ'}
                  </ActionButton>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {myItem && (
        <Card className="bg-primary/5 border border-primary/30">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-muted-foreground">Моя территория</div>
              <div className="font-semibold flex items-center gap-2 flex-wrap">
                {myItem.manager_name || `#${myItem.manager_user_id.slice(0, 8)}`}
                {myItem.tu_role_end_date && (
                  <Badge text={`в должности по ${fmtIsoDate(myItem.tu_role_end_date)}`} variant="warning" />
                )}
              </div>
              <div className="text-sm text-muted-foreground">
                Франчайзи: {myItem.partners_count} · Студий: {myItem.studios_count}
              </div>
            </div>
            <ActionButton
              tone="primary"
              onClick={() => navigate(`/management/territorial-managers/${myItem.manager_user_id}`)}
            >
              Открыть
            </ActionButton>
          </div>
        </Card>
      )}

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Поиск по ФИО или @username"
          className="w-full pl-9 pr-3 py-2 border border-border rounded-md bg-background text-sm"
        />
      </div>

      {!periods.loading && periods.currentPeriod && (
        <Card className="py-2">
          <PeriodSelector
            currentPeriod={periods.currentPeriod}
            onPrevPeriod={periods.goToPrevPeriod}
            onNextPeriod={periods.goToNextPeriod}
            canGoPrev={periods.canGoPrev}
            canGoNext={periods.canGoNext}
            showViewModeSelector
            isCurrentPeriod={periods.isCurrentPeriod}
            periodFilterValue={selectedTimeFilter}
            periodFilterOptions={timeFilterOptions}
            onPeriodFilterChange={setSelectedTimeFilter}
            selectedRange={selectedRange}
            compact
          />
        </Card>
      )}

      {(loading || periods.loading || weekLoading) && <Card><div className="text-sm text-muted-foreground">Загрузка…</div></Card>}
      {periods.error && <Card><div className="text-sm text-destructive">Ошибка периодов: {periods.error}</div></Card>}
      {error && <Card><div className="text-sm text-destructive">Ошибка: {error}</div></Card>}

      {!loading && !error && filtered.length === 0 && (
        <Card>
          <div className="text-sm text-muted-foreground">
            Территориальные управляющие пока не назначены.
            {isAdvanced && ' Откройте «Франчайзи и ТУ», чтобы назначить.'}
          </div>
        </Card>
      )}

      {filtered.length > 0 && (
        <div className="bg-card rounded-card shadow-card overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-[minmax(200px,1.4fr)_repeat(7,minmax(92px,1fr))] gap-2 px-3 py-2 border-b border-border bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <div>Управляющий</div>
              <div className="text-right">План-факт</div>
              <div className="text-right">Сред. на студию</div>
              <div className="text-right">Сред. на франчайзи</div>
              <div className="text-right">Лидов на студию</div>
              <div className="text-right">CR запись</div>
              <div className="text-right">CR доход.</div>
              <div className="text-right">CR продажа</div>
            </div>
            {filtered.map(item => {
              const kpi = kpiByManager.get(item.manager_user_id) ?? null
              const tone = planCompletionTone(kpi?.plan_completion_pct ?? null)
              return (
                <button
                  key={item.manager_user_id}
                  type="button"
                  onClick={() => navigate(`/management/territorial-managers/${item.manager_user_id}`)}
                  className="w-full text-left grid grid-cols-[minmax(200px,1.4fr)_repeat(7,minmax(92px,1fr))] gap-2 px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted/30"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-sm truncate">
                        {item.manager_name || `#${item.manager_user_id.slice(0, 8)}`}
                      </span>
                      {item.manager_user_id === user?.id && (
                        <Badge text="Вы" variant="default" className="text-[10px] px-1.5 py-0" />
                      )}
                      {item.tu_role_end_date && (
                        <Badge text={`в должности по ${fmtIsoDate(item.tu_role_end_date)}`} variant="warning" className="text-[10px] px-1.5 py-0" />
                      )}
                      {item.job_title === 'territorial_manager' && item.partners_count === 0 && (
                        <Badge text="без франчайзи" variant="neutral" className="text-[10px] px-1.5 py-0" />
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                      {item.tg_username && <span>@{item.tg_username}</span>}
                      <span className="flex items-center gap-0.5">
                        <Users size={11} />{item.partners_count}
                      </span>
                      <span className="flex items-center gap-0.5">
                        <Building2 size={11} />{item.studios_count}
                      </span>
                    </div>
                  </div>

                  <KpiCell
                    primary={kpi?.plan_completion_pct != null ? formatPercentage(kpi.plan_completion_pct) : '—'}
                    secondary={kpi && kpi.plan_gross_revenue > 0
                      ? `${formatCurrency(kpi.fact_gross_revenue_rub)} / ${formatCurrency(kpi.plan_gross_revenue)}`
                      : kpi ? 'нет плана' : ''}
                    tone={tone}
                  />
                  <KpiCell
                    primary={kpi?.avg_revenue_per_studio != null ? formatCurrency(kpi.avg_revenue_per_studio) : '—'}
                    secondary={kpi ? `${kpi.studio_count} студ.` : ''}
                  />
                  <KpiCell
                    primary={kpi?.avg_revenue_per_partner != null ? formatCurrency(kpi.avg_revenue_per_partner) : '—'}
                    secondary={kpi ? `${kpi.partner_count} парт.` : ''}
                  />
                  <KpiCell
                    primary={kpi
                      ? (kpi.studio_count > 0 ? formatNumberRounded(kpi.fact_leads_count / kpi.studio_count) : '—')
                      : '—'}
                    secondary={kpi
                      ? `${formatNumberRounded(kpi.fact_leads_count)} / ${formatNumberRounded(kpi.plan_leads_count ?? 0)}`
                      : ''}
                  />
                  <KpiCell
                    primary={kpi?.booking_conversion_pct != null ? formatPercentage(kpi.booking_conversion_pct) : '—'}
                    secondary={kpi && kpi.fact_leads_count > 0
                      ? `${formatNumberRounded(kpi.fact_appointments_count)} / ${formatNumberRounded(kpi.fact_leads_count)}`
                      : kpi ? 'нет лидов' : ''}
                  />
                  <KpiCell
                    primary={kpi?.visit_conversion_pct != null ? formatPercentage(kpi.visit_conversion_pct) : '—'}
                    secondary={kpi && kpi.fact_appointments_count > 0
                      ? `${formatNumberRounded(kpi.fact_new_visitors_count)} / ${formatNumberRounded(kpi.fact_appointments_count)}`
                      : kpi ? 'нет записей' : ''}
                  />
                  <KpiCell
                    primary={kpi?.sales_conversion_pct != null ? formatPercentage(kpi.sales_conversion_pct) : '—'}
                    secondary={kpi && kpi.fact_new_visitors_count > 0
                      ? `${formatNumberRounded(kpi.fact_new_sales_count)} / ${formatNumberRounded(kpi.fact_new_visitors_count)}`
                      : kpi ? 'нет визитов' : ''}
                  />
                </button>
              )
            })}
          </div>
        </div>
      )}

      {formerItems.length > 0 && (
        <Card>
          <button
            type="button"
            className="w-full flex items-center gap-2 text-left"
            onClick={() => setFormerOpen(v => !v)}
          >
            {formerOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className="text-sm font-medium">Снятые с должности</span>
            <span className="text-sm text-muted-foreground tabular-nums">{formerItems.length}</span>
            <span className="text-xs text-muted-foreground">— в карточке можно поправить дату снятия и даты по франчайзи</span>
          </button>
          {formerOpen && (
            <div className="mt-3 space-y-1">
              {formerItems.map(item => (
                <button
                  key={item.manager_user_id}
                  type="button"
                  onClick={() => navigate(`/management/territorial-managers/${item.manager_user_id}`)}
                  className="w-full text-left flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border/70 px-2 py-1.5 hover:bg-muted/30"
                >
                  <span className="text-sm font-medium">{item.manager_name || `#${item.manager_user_id.slice(0, 8)}`}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {item.tu_role_end_date
                      ? `последний день ${fmtIsoDate(item.tu_role_end_date)}`
                      : 'дата снятия не записана'}
                  </span>
                  <span className="text-[11px] text-muted-foreground flex items-center gap-0.5">
                    <Users size={11} />{item.partners_count} франч. за всё время
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

function fmtIsoDate(iso: string): string {
  const [yyyy, mm, dd] = iso.split('-')
  return `${dd}.${mm}.${yyyy}`
}

interface KpiCellProps {
  primary: string
  secondary?: string
  tone?: 'success' | 'warning' | 'error' | 'neutral'
}

function KpiCell({ primary, secondary, tone = 'neutral' }: KpiCellProps) {
  const valueClass =
    tone === 'success' ? 'text-success'
    : tone === 'warning' ? 'text-primary'
    : tone === 'error' ? 'text-error'
    : 'text-foreground'
  return (
    <div className="text-right">
      <div className={`text-sm font-semibold tabular-nums ${valueClass}`}>{primary}</div>
      {secondary && (
        <div className="text-[10px] text-muted-foreground tabular-nums truncate">{secondary}</div>
      )}
    </div>
  )
}
